import { z } from 'zod';

const ALLOWED_PROTOCOLS = new Set(['https:', 'http:', 'blob:']);

/**
 * Sanitizes a user-supplied URL, returning it only if it uses an allowed
 * protocol (https, http, or blob) or is a safe relative path.
 *
 * Prevents XSS via `javascript:` / `data:` URLs and strips ASCII control
 * characters that browsers may silently ignore during URL parsing.
 *
 * Returns `undefined` when the URL is unsafe or unparseable.
 */
export function sanitizeUrl(url: string): string | undefined {
  // Strip ASCII control characters (0x00–0x1F, 0x7F) for defense-in-depth.
  const cleaned = url.replaceAll(/[\u0000-\u001F\u007F]/g, '');

  // Allow safe relative paths, but block protocol-relative (//) and
  // backslash variants that browsers normalize to /.
  if (cleaned.startsWith('/')) {
    return /^\/[/\\]/.test(cleaned) ? undefined : cleaned;
  }

  try {
    const parsed = new URL(cleaned);
    return ALLOWED_PROTOCOLS.has(parsed.protocol) ? cleaned : undefined;
  } catch {
    return undefined;
  }
}

export const zHttpUrl = z.url({
  protocol: /^https?$/,
  hostname: z.regexes.domain,
});

/**
 * https only, on a real domain — for URLs we hand to someone else's browser
 * (a prototype a participant opens, a page framed beside an interview
 * question). Mirrors what core-api's media DTOs enforce with
 * `@IsUrl({ protocols: ['https'], require_protocol: true })`, so a URL that
 * passes here is one the server will accept.
 *
 * Stricter than {@link zHttpUrl} on purpose: it rejects plain http, a
 * hostname with no TLD (`https://exa`), and hosts only the researcher can
 * reach (`localhost`, a LAN address) — none of which resolve for a
 * participant on another machine.
 */
export const zHttpsUrl = z.url({
  protocol: /^https$/,
  hostname: z.regexes.domain,
});
